import { useTranslation } from 'react-i18next';
import { Button, Tabs, TabsList, TabsTrigger } from '@monksflow/monks-ui';
import { Plus, Trash2 } from 'lucide-react';
import { SearchInput } from '@/shared/ui/SearchInput';
import {
  TAXONOMY_STATUS_TABS,
  type TaxonomyStatusFilter,
} from '../../constants/taxonomy.constants';

interface TaxonomyToolbarProps {
  /** Active status-filter tab. */
  statusFilter: TaxonomyStatusFilter;
  /** Fires when the user picks a different status tab. */
  onStatusFilterChange: (value: TaxonomyStatusFilter) => void;
  /** Controlled search input value. Parent owns debounce + filtering. */
  search: string;
  /** Fires synchronously on each keystroke with the new input value. */
  onSearchChange: (value: string) => void;
  /** Selected rows; when > 0 the destructive bulk action appears. */
  selectedCount?: number;
  /** Fires when the user clicks the bulk-delete action. */
  onBulkDelete?: () => void;
  /** Fires when the user clicks the "Add schema" CTA. Omitted hides it. */
  onCreateClick?: () => void;
}

/**
 * Toolbar for the Taxonomy list view: status-filter tabs on the left, search +
 * "Add schema" CTA on the right. Pure controlled component — the parent owns
 * the status filter, the search string (+ debounce), and create intent.
 *
 * "Import from Claravine" from the prototype is intentionally omitted until the
 * Claravine integration is scoped.
 */
export function TaxonomyToolbar(props: TaxonomyToolbarProps) {
  const { t } = useTranslation('knowledge');

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 px-section py-4">
      <Tabs
        value={props.statusFilter}
        onValueChange={(value) => props.onStatusFilterChange(value as TaxonomyStatusFilter)}
      >
        <TabsList>
          {TAXONOMY_STATUS_TABS.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="flex items-center gap-3">
        <SearchInput
          placeholder={t('taxonomy.taxonomyToolbar.searchSchemas')}
          aria-label={t('taxonomy.taxonomyToolbar.searchSchemasName')}
          value={props.search}
          onChange={props.onSearchChange}
          className="w-72"
        />
        {(props.selectedCount ?? 0) > 0 && props.onBulkDelete && (
          <Button variant="destructive" size="default" onClick={props.onBulkDelete}>
            <Trash2 className="size-4" />
            {t('taxonomy.taxonomyToolbar.deleteSchemas', { count: props.selectedCount ?? 0 })}
          </Button>
        )}
        {props.onCreateClick && (
          <Button size="default" onClick={props.onCreateClick}>
            <Plus className="size-4" />
            {t('taxonomy.taxonomyToolbar.addSchema')}
          </Button>
        )}
      </div>
    </div>
  );
}

TaxonomyToolbar.displayName = 'TaxonomyToolbar';
